import React, { useEffect, useState } from 'react'
import { View, KeyboardAvoidingView, Platform, Keyboard, ActivityIndicator, useWindowDimensions, BackHandler, DeviceEventEmitter, ScrollView } from 'react-native'
import { ThemeConsumer } from '@rneui/themed'
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs'
import { FormikProps, useFormik } from 'formik'
import Popover from 'react-native-popover-view/dist/Popover'
import * as Yup from 'yup'

import { config } from '../../shared/environment'

import CommonHeader from '../../shared/commonComponents/commonHeader'
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox'

import ViewProduct from './viewProduct'

import { getScreenPermission, useAppDispatch, useAppSelector } from '../../redux/hooks'
import { useAppNavigation, useAppRoute } from '../../types/@react-navigation/navigation'
import { createPosBrand, onePosBrand, updatePosBrand } from '../../redux/slices/brandSlice'
import { checkDuplicatesRedux, pageDirty } from '../../redux/slices/commonSlice'

import { CheckDuplicatesProps } from '../customerGroups/customerGroupModule'
import { ZenButton, ZenInput, ZenLoader, ZenToast } from 'zen-core-elements'
import { CustomModal } from '../../shared/commonComponents/customModal'
import { GetOnePosBrand } from './brandModule'
import { shallowEqual } from 'react-redux'

const AddEditBrand = () => {
    const { isWrite: isBrandWrite } = useAppSelector(getScreenPermission("BrandStack"), shallowEqual)

    //Variable for Top tap navigation
    const TopTab = createMaterialTopTabNavigator()
    const dispatch = useAppDispatch()
    const { createPosBrand: { loading: createPosBrandLoading }, updatePosBrand: { loading: updatePosBrandLoading } } = useAppSelector((state) => state?.brands)
    const { loading: getOneBrandLoader, value: onePosBrandData } = useAppSelector((state) => state?.brands?.onePosBrand)
    const id = useAppRoute('AddEditBrand').params?.id
    const navigation = useAppNavigation()
    const { width } = useWindowDimensions()
    const [leavePopoverVisible, setLeavePopoverVisible] = useState(false)
    //variable which holds route data

    const [brandDetails, setBrandDetails] = useState<GetOnePosBrand>()

    const [duplicateLoading, setDuplicateLoading] = useState(false)
    const [isDuplicateName, setIsDuplicateName] = useState(false)

    useEffect(() => {
        if (onePosBrandData?.id == id) {
            setBrandDetails(onePosBrandData!!)
        } else {
            if (id == undefined) {
                setBrandDetails(undefined)
            } else {
                getOneBrandData()
            }
        }
    }, [onePosBrandData, id])

    const getOneBrandData = async () => {
        await dispatch(onePosBrand({ id, storeId: config?.storeId }))
    }

    const YupValidation = Yup?.object()?.shape({
        brandName: Yup.string()
            .required("Brand name is required*")
            .max(120, 'Brand Name should not exceed more than 120 character')
    })
    const formik = useFormik({
        initialValues: {
            brandName: brandDetails?.brandname || "",
            description: brandDetails?.description || ""
        },
        validationSchema: YupValidation,
        enableReinitialize: true,
        onSubmit: (values) => {
            if (!isDuplicateName) {
                addEditBrandProducts(values)
            } else {
                ZenToast({ type: 'warning', message: 'Please enter a unique brand name' })
            }
        }
    })

    useEffect(() => {
        if (formik?.dirty) {
            dispatch(pageDirty(true))
        }
        else {
            dispatch(pageDirty(false))
        }
    }, [formik?.dirty])
    useEffect(() => {
        const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
            if (formik?.dirty) {
                setLeavePopoverVisible(true)
                return true;
            }
            else {
                navigation.goBack()
            }
            return true;
        });

        return () => backHandler.remove();
    }, [formik]);

    useEffect(() => {
        if (formik?.values?.brandName && formik?.values?.brandName !== brandDetails?.brandname) {
            setDuplicateLoading(true)
            const debounceText = setTimeout(() => {
                checkDuplicateName()
            }, 1000);
            return () => clearTimeout(debounceText)
        }
        else {
            setDuplicateLoading(false)
            setIsDuplicateName(false)
        }
    }, [formik?.values?.brandName])

    useEffect(() => {
        const emitdata = DeviceEventEmitter?.addListener("saveBrandData", (productIds: number[]) => {
            addEditBrandProducts(formik?.values, productIds, true)
        })

        return () => {
            emitdata?.remove()
        }
    }, [formik])

    const checkDuplicateName = async () => {
        const data: CheckDuplicatesProps = {
            "value": formik?.values?.brandName,
            "tableName": "brands",
            "columnName": "brandName",
            "id": brandDetails?.id || null,
            "storeId": true
        }
        dispatch(checkDuplicatesRedux({ storeId: config?.storeId, data: data })).unwrap().then((response) => {
            setDuplicateLoading(false)
            setIsDuplicateName(response?.result?.alreadyExists)
        }).catch((error: any) => {
            console.log("check duplicates redux", error)
        })
    }

    //method for popover confirmation 
    const confirmation = (item: { id: number }) => {
        setLeavePopoverVisible(false)
        if (item?.id == 2) {
            navigation?.goBack()
            dispatch(pageDirty(false))
        }
    }

    const addEditBrandProducts = (values: { brandName: string, description: string }, selectedProducts?: number[], isevent?: boolean) => {
        let payload = {
            "brandName": values?.brandName,
            "description": values?.description || null,
            "searchKeyword": null,
            "imageUrl": null,
            "isPos": true,
            "isWeb": false,
            "medium": "POS",
            "selectedProducts": selectedProducts || []
        }
        if (id) {
            updateBrand(payload, id, isevent)
        } else {
            createBrand(payload, isevent)
        }
    }

    const createBrand = (data: any, isevent?: boolean) => {
        dispatch(createPosBrand({ data: data, storeId: config?.storeId })).unwrap().then((response) => {
            if (response?.result?.brandName) {
                ZenToast({
                    type: "success",
                    message: "Brand created successfully"
                })
                formik.resetForm()
                dispatch(pageDirty(false))
            } else {
                ZenToast({
                    type: "warning",
                    message: response?.message
                })
            }
        }).catch((error) => {
            ZenToast({
                type: "error",
                message: "Failed to create the brand details"
            })
            console.log("Error while creating the brand", error)
        }).finally(() => {
            navigation.pop(isevent ? 2 : 1)
        })
    }

    const updateBrand = (data: any, brandId: number, isevent?: boolean) => {
        dispatch(updatePosBrand({ data: data, storeId: config?.storeId, id: brandId })).unwrap().then((response) => {
            if (response) {
                ZenToast({
                    type: "success",
                    message: "Brand updated successfully"
                })
                dispatch(pageDirty(false))
            }
        }).catch((error) => {
            ZenToast({
                type: "error",
                message: "Failed to update the brand details"
            })
            console.log("Error while updating the brand details", error)
        }).finally(() => {
            navigation.pop(isevent ? 2 : 1)
        })
    }

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} onStartShouldSetResponder={() => { Keyboard?.dismiss(); return false }} enabled style={theme?.addEditCustomerStyle?.outerContainer}>
                    <CommonHeader headerName={id ? "Edit Brand details" : "Add brand details"} backIcon backIconOnpress={() => { formik?.dirty ? setLeavePopoverVisible(true) : navigation?.goBack() }}
                        rightComponent={
                            isBrandWrite ? <ZenButton
                                title={id ? "Update" : "Save"}
                                titleColor={theme?.light}
                                disabled={(!formik?.dirty || !formik?.isValid || duplicateLoading || isDuplicateName) ? true : false}
                                buttonStyle={{ backgroundColor: theme?.primary, borderRadius: 10, paddingHorizontal: 10 }}
                                onPress={() => formik.handleSubmit()}
                            /> : undefined
                        } />
                    <TopTab.Navigator
                        initialRouteName="Brands"
                        screenOptions={{
                            lazy: true,
                            tabBarLabelStyle: { fontSize: theme.largerFontSize, fontFamily: theme.bold, textTransform: 'capitalize' },
                            tabBarIndicatorStyle: { backgroundColor: theme.primary },
                            tabBarActiveTintColor: theme.primary,
                            tabBarInactiveTintColor: theme.lightFontColor,
                            tabBarAndroidRipple: { borderless: false }
                        }}
                        backBehavior="none"
                    >
                        <TopTab.Screen
                            name="Brands"
                            options={{ tabBarLabel: 'Brands', title: "Brands" }}
                            children={() => <BasicDetails isBrandWrite={isBrandWrite} formik={formik} duplicateLoading={duplicateLoading} isDuplicateName={isDuplicateName} />}
                        />
                        <TopTab.Screen
                            name="ProductsTap"
                            options={{ tabBarLabel: 'Products', title: "Products" }}
                            component={ViewProduct}
                            listeners={{
                                tabPress: (e) => {
                                    if (!formik.values.brandName?.trim() || isDuplicateName) {
                                        e.preventDefault();
                                        ZenToast({ type: "warning", message: "Please enter a brand name." });
                                    }
                                },
                            }}
                            initialParams={{ id: id, isAll: false, formikValues: formik.values }} />
                    </TopTab.Navigator>

                    <CustomModal transparent visible={createPosBrandLoading || updatePosBrandLoading || getOneBrandLoader}>
                        <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={theme?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
                    </CustomModal>
                    {/* For leave confirmation */}
                    <Popover isVisible={leavePopoverVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}>
                        <CommonDialogBox type="EDIT" replaceValue="page" confirmation={confirmation} />
                    </Popover>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    )
}

export const BasicDetails = React.memo(({ formik, duplicateLoading, isDuplicateName, isBrandWrite }: { formik: FormikProps<{ brandName: string, description: string }>, duplicateLoading: boolean, isDuplicateName: boolean, isBrandWrite: boolean }) => {

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <View style={{ padding: 10, gap: 10 }}>
                    <ScrollView showsVerticalScrollIndicator={false}>
                        <View style={{ backgroundColor: theme?.light, borderRadius: 10, paddingHorizontal: 5, paddingVertical: 10, }}>

                            <View style={{ paddingTop: 15 }}>
                                <ZenInput
                                    value={formik?.values?.brandName}
                                    disabled={!isBrandWrite}
                                    autoCapitalize={"none"}
                                    onChangeText={(text: string) => { formik?.setFieldValue("brandName", text) }}
                                    label={"Brand Name*"}
                                    onBlur={() => {
                                        formik?.setFieldTouched("brandName", true)
                                    }}
                                    rightIcon={
                                        duplicateLoading ? <ActivityIndicator size={'small'} color={theme?.primary} /> : <></>
                                    }
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8, }}
                                    errorMessage={formik?.errors?.brandName && !formik?.isValid && formik?.touched?.brandName ? formik?.errors?.brandName?.toString() : isDuplicateName ? "Brand name already exists!" : ""}
                                    errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: (formik?.errors?.brandName && !formik?.isValid && formik?.touched?.brandName) || isDuplicateName ? 10 : 0 }}
                                    type='outlined'
                                />
                                <ZenInput
                                    value={formik?.values?.description}
                                    autoCapitalize={"none"}
                                    disabled={!isBrandWrite}
                                    onChangeText={(text: string) => { formik?.setFieldValue("description", text) }}
                                    label={"Description"}
                                    multiline={true}
                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                    containerStyle={{ paddingHorizontal: 15, }}
                                    inputContainerStyle={{ paddingHorizontal: 8, height: 80 }}
                                    type='outlined'
                                />
                            </View>
                        </View>
                    </ScrollView>
                </View>
            )}
        </ThemeConsumer>
    )
})

export default React.memo(AddEditBrand)